/**
 * CronConvertPanel — "Convert a cron → workflow" (ADR-080 ext / ADR-066), hosted
 * in the Skills view. Pick a prompt-cron; the workflow authoring copilot drafts a
 * deterministic dsl activity from the cron's prompt (baking in a delivery step
 * when the cron had `deliver:`); review; propose — which commits the activity AND
 * rewrites the cron `prompt:`→`activity:` (one PR + in-app improvement).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  AuthorTestsFailingError,
  CopilotNotConfiguredError,
  type FailingCheck,
  type PromptCron,
  type ProposeSkillResult,
  type ValidateActivityResult,
} from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { PendingReferences } from '../components/PendingReferences'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export function CronConvertPanel({ canPropose, onClose }: { canPropose: boolean; onClose: () => void }) {
  const [crons, setCrons] = useState<PromptCron[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [sel, setSel] = useState<PromptCron | null>(null)
  const [phase, setPhase] = useState<'idle' | 'drafting' | 'draft' | 'proposing' | 'done'>('idle')
  const [activityName, setActivityName] = useState('')
  const [draftYaml, setDraftYaml] = useState('')
  const [validation, setValidation] = useState<ValidateActivityResult | null>(null)
  const [rationale, setRationale] = useState('')
  const [proposed, setProposed] = useState<ProposeSkillResult | null>(null)
  // ADR-107 P3: the fleet's own author tests failed against this proposed
  // set (409 author_tests_failing) — rendered via the SAME FailingChecksPanel
  // ADR-1027's PR review uses, not the generic err toast.
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])

  useEffect(() => {
    let live = true
    // ADR-103: listPromptCrons() carries no ownership flag of its own, so cross-
    // reference each cron's agent against the core-agent set (fetched
    // separately) and exclude a core agent's crons — this picker only
    // authors conversions, and a core agent's cron can never be proposed.
    Promise.all([api.listPromptCrons(), api.listCoreAgents().catch(() => ({ agents: [] }))])
      .then(([cronsRes, coreRes]) => {
        if (!live) return
        const coreAgents = new Set(coreRes.agents ?? [])
        setCrons((cronsRes.crons ?? []).filter(c => !coreAgents.has(c.agent)))
        setLoading(false)
      })
      .catch(e => { if (live) { setErr(em(e)); setLoading(false) } })
    return () => { live = false }
  }, [])

  const selectCron = (c: PromptCron | null) => {
    setSel(c)
    setPhase('idle'); setDraftYaml(''); setValidation(null); setRationale(''); setProposed(null); setErr(null); setFailingChecks([])
    setActivityName(c ? c.name : '')
  }

  const draftWorkflow = async () => {
    if (!sel) return
    setPhase('drafting'); setErr(null); setFailingChecks([])
    const seed =
      `Author a deterministic dsl workflow (activity) that performs this scheduled task, so it runs reliably without a model turn each time.\n\n` +
      `The cron's current prompt:\n"""\n${sel.prompt}\n"""\n` +
      (sel.deliver
        ? `\nThis cron auto-delivered its result to ${sel.deliver}; the activity engine does NOT auto-deliver, so the workflow MUST finish with an explicit step that delivers the result to ${sel.deliver}.`
        : '')
    try {
      const r = await api.copilotAuthor(sel.agent, [{ role: 'user', content: seed }])
      setDraftYaml(r.draft)
      setValidation(r.validation)
      setRationale(r.rationale)
      setPhase('draft')
    } catch (e) {
      setErr(e instanceof CopilotNotConfiguredError ? 'The copilot is not configured for this fleet (no Anthropic key).' : em(e))
      setPhase('idle')
    }
  }

  const propose = async () => {
    if (!sel) return
    setPhase('proposing'); setErr(null); setFailingChecks([])
    try {
      const r = await api.proposeCronConversion(sel.agent, sel.name, sel.layer, { name: activityName.trim(), yaml: draftYaml })
      setProposed(r); setPhase('done')
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else setErr(em(e))
      setPhase('draft')
    }
  }

  const busy = phase === 'drafting' || phase === 'proposing'
  const draftValid = validation?.ok === true && draftYaml.trim() !== '' && activityName.trim() !== ''

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/40 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-purple-800">Convert a cron → workflow</h3>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-800">✕ close</button>
      </div>
      <p className="text-xs text-gray-600">
        Turn a scheduled <span className="font-medium">prompt-cron</span> (a model turn each run) into a deterministic <span className="font-medium">workflow</span>. The copilot drafts it from the cron's prompt; proposing commits the workflow and rewires the cron to run it (<span className="font-mono">activity:</span>).
      </p>

      {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words max-h-40 overflow-auto">{err}</p>}
      {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

      {loading ? (
        <p className="text-xs text-gray-400 animate-pulse">Loading prompt-crons…</p>
      ) : crons.length === 0 ? (
        <p className="text-xs text-gray-500">No prompt-crons to convert — every cron already runs an activity (or there are none).</p>
      ) : (
        <label className="text-xs text-gray-600 block">Prompt-cron
          <select
            value={sel ? `${sel.layer}:${sel.agent}:${sel.name}` : ''}
            onChange={e => selectCron(crons.find(c => `${c.layer}:${c.agent}:${c.name}` === e.target.value) ?? null)}
            className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 min-w-[22rem]"
          >
            <option value="">— pick a cron —</option>
            {crons.map(c => (
              <option key={`${c.layer}:${c.agent}:${c.name}`} value={`${c.layer}:${c.agent}:${c.name}`}>
                {c.agent} / {c.name} ({c.layer}, {c.schedule})
              </option>
            ))}
          </select>
        </label>
      )}

      {sel && (
        <div className="rounded border border-gray-200 bg-white/70 p-2 text-xs text-gray-700 space-y-1">
          <div><span className="text-gray-400">schedule:</span> <span className="font-mono">{sel.schedule}</span>{sel.deliver && <> · <span className="text-gray-400">delivers to:</span> <span className="font-mono">{sel.deliver}</span></>}</div>
          <div className="text-gray-400">prompt:</div>
          <pre className="whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded p-2 max-h-40 overflow-auto">{sel.prompt}</pre>
          {phase === 'idle' && (
            <button type="button" onClick={() => void draftWorkflow()} disabled={busy}
              className="mt-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
              Draft the workflow
            </button>
          )}
        </div>
      )}

      {phase === 'drafting' && <p className="text-xs text-purple-700 animate-pulse">Drafting the workflow from the cron's prompt…</p>}

      {(phase === 'draft' || phase === 'proposing') && (
        <div className="space-y-2">
          {rationale && <p className="text-xs text-gray-600 italic">{rationale}</p>}
          <label className="text-xs text-gray-600 block">Workflow name
            <input type="text" value={activityName} onChange={e => setActivityName(e.target.value)}
              className="mt-1 block text-sm font-mono border border-gray-300 rounded px-2 py-1 min-w-[16rem]" />
          </label>
          <textarea value={draftYaml} onChange={e => { setDraftYaml(e.target.value); setValidation(null) }} rows={14}
            className="w-full text-xs font-mono border border-gray-300 rounded px-2 py-1" />
          {validation && (
            validation.ok
              ? <p className="text-xs text-green-700">✓ valid</p>
              : <ul className="text-xs text-red-700 list-disc list-inside">{validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          )}
          {validation && <PendingReferences pending={validation.pending} className="text-xs" />}
          {!validation && <p className="text-[10px] text-gray-400">Edited — re-draft or note the server re-validates on propose.</p>}
          <div className="flex items-center gap-2">
            {canPropose ? (
              <button type="button" disabled={busy || !draftValid || !!proposed} onClick={() => void propose()}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                title={!draftValid ? 'The workflow must be valid and named before proposing' : undefined}>
                {phase === 'proposing' ? 'Running checks…' : 'Propose (workflow + rewire cron)'}
              </button>
            ) : <span className="text-xs text-gray-500">Only a fleet owner can propose this conversion.</span>}
            <button type="button" onClick={() => void draftWorkflow()} disabled={busy}
              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Re-draft</button>
          </div>
        </div>
      )}

      {phase === 'done' && proposed && (
        proposed.improvement_warning ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
            <p>{proposed.improvement_warning}</p>
          </div>
        ) : (
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
            <p>Sent for review — the new workflow + the cron rewired to run it. Nothing ships until you approve it.</p>
            {proposed.improvement_id
              ? <Link to={`/improvements/${encodeURIComponent(proposed.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
              : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
          </div>
        )
      )}
    </div>
  )
}
